import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { App, Button, Tag } from 'antd';
import { useForm, FormProvider } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import resourcesMap from '../../../constants/ApplicationResourcesMapping.json';
import {
  getVehicleResources,
  getVehicleResourceMaps,
  mapConfigIdToName,
  mergeApplicationRanks,
} from '../../../selectors/applications/applicationsSelector';
import ApplicationDetails from './ApplicationDetails';
import { ApplicationState } from '../../../reducers';
import { AsyncDispatch } from '../../../../types/global';
import { Application, VcdbVersion } from '../../../../types/application';
import {
  mapConfigToApplication,
  convertConfigNameToIds,
  getConfigGroupName,
  getSingularConfigGroupName,
  convertConfigNameToId,
} from '../../../utils/ApplicationUtils';
import {
  fetchRankedApplications,
  fetchApplicationResources,
} from '../../../actions/items/application/fetch';
import {
  addCustomSubConfig,
  changeCustomVehicle,
  updateCustomVehicleConfigs,
} from '../../../actions/items/application/update';
import {
  deleteCustomConfig,
  deleteCustomSubConfig,
} from '../../../actions/items/application/delete';
import DrawerSave from '../../global/drawer/DrawerSave';
import Page from '../../global/page/Page';
import { hasPermission } from '../../../utils/Permissions';
import { intercomEvent } from '../../../utils/IntercomUtils';
import FormRouterPrompt from '../../global/Forms/FormRouterPrompt';
import { useAppDispatch } from '../../../store/ReduxStore';

const resourcesMapping = resourcesMap as { [key: string]: string };
type FormValues = {
  application: Application;
  linkConfigs: string[];
  createVehicle: boolean;
  selectedVcdb: number;
  changeConfigs: { configName: string; id: number }[];
};
type VcdbEditProps = {
  visible?: boolean;
  page?: boolean;
  onClose?: (dirty: boolean, force?: boolean) => void;
};
const VcdbEdit: React.FC<VcdbEditProps> = ({ visible, page, onClose }) => {
  const { t } = useTranslation();
  const dispatch: AsyncDispatch = useAppDispatch();
  const { modal, message } = App.useApp();
  const { rankedApplications, resources, resourceMaps, applicationStructure, vcdbVersions, user } =
    useSelector((state: ApplicationState) => ({
      rankedApplications: state.items.application.rankedApplications,
      resources: getVehicleResources(state),
      resourceMaps: getVehicleResourceMaps(state),
      applicationStructure: state.resources.data.vehicle_structure,
      vcdbVersions: state.items.application.vcdbVersions as VcdbVersion[],
      user: state.user.user,
    }));
  const [selectedVcdb, setSelectedVcdb] = useState<number | undefined>(
    vcdbVersions.filter(vcdb => vcdb.account_id !== null)[0]?.id
  );
  const [getRecommendations, setRecommendations] = useState(false);
  const [selectedSubconfig, setSelectedSubconfig] = useState<string | null>('vehicle_bases');
  const [pendingConfigCount, setPendingConfigCount] = useState<number | undefined>(undefined);
  const [vehicleEquipmentType, setVehicleEquipmentType] = useState('vehicle');
  const [changeConfigActive, setChangeConfigActive] = useState(false);
  const [status, setStatus] = useState<'EDIT' | 'SUCCESS' | 'ERROR' | null>(null);

  const canManageVcdb = React.useMemo(() => hasPermission(user, 'can_manage_custom_vcdb'), [user]);

  const validationSchema = Yup.object().shape({
    selectedVcdb: Yup.number().required(t('validation:required')),
    application: Yup.object(),
    linkConfigs: Yup.array(),
    createVehicle: Yup.boolean(),
    changeConfigs: Yup.array(),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      application: {} as Application,
      linkConfigs: [],
      createVehicle: false,
      selectedVcdb,
      changeConfigs: [],
    },
    resolver: yupResolver(validationSchema) as any,
    mode: 'onTouched',
  });

  const { handleSubmit, setValue, reset, formState, watch } = formMethods;

  const values = watch();

  React.useEffect(() => {
    if (page && status === null) {
      setStatus('EDIT');
    }
  }, [page, status]);

  const selectMake = (makeId: number) => {
    const makeIds = values.application.make_ids?.includes(makeId) ? [] : [makeId];
    const updatedApplication = mapConfigToApplication(values.application, 'make_ids', makeIds);
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectModel = (modelId: number) => {
    const modelIds = values.application.model_ids?.includes(modelId) ? [] : [modelId];
    const updatedApplication = mapConfigToApplication(values.application, 'model_ids', modelIds);
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectType = (id: number) => {
    const typeIds = values.application.vehicle_type_ids?.includes(id) ? [] : [id];
    let updatedApplication = mapConfigToApplication(
      values.application,
      'vehicle_type_ids',
      typeIds
    );
    if (typeIds.length > 0) {
      const type = resources.model_types.find((t: any) => t.id === id);
      if (type) {
        const group = resources.model_type_groups.find((g: any) => g.name === type.vehicle_group);
        if (group) {
          updatedApplication = mapConfigToApplication(updatedApplication, 'vehicle_group_ids', [
            group.id,
          ]);
        }
      }
    }
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectSubmodels = (ids: any) => {
    const submodelIds = ids instanceof Array ? ids : [ids];
    let selectedSubmodelIds = values.application.sub_model_ids || [];
    if (submodelIds.length === 1 && selectedSubmodelIds.length === 1) {
      // select or unselect single submodel
      selectedSubmodelIds = selectedSubmodelIds[0] === submodelIds[0] ? [] : submodelIds;
    } else {
      selectedSubmodelIds = submodelIds;
    }
    const updatedApplication = mapConfigToApplication(
      values.application,
      'sub_model_ids',
      selectedSubmodelIds
    );
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectRegion = (ids: any) => {
    const regionIds = ids instanceof Array ? ids : [ids];
    let selectedRegionIds = values.application.region_ids || [];
    if (regionIds.length === 1 && selectedRegionIds.length === 1) {
      // select or unselect single region
      selectedRegionIds = selectedRegionIds[0] === regionIds[0] ? [] : regionIds;
    } else {
      selectedRegionIds = regionIds;
    }
    const updatedApplication = mapConfigToApplication(
      values.application,
      'region_ids',
      selectedRegionIds
    );
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectYears = (year: number | number[]) => {
    let years = year instanceof Array ? Array.from(new Set(year)) : [year];
    const selectedYears = values.application.years || [];
    if (years.length === 1 && selectedYears.length === 1) {
      years = years[0] === selectedYears[0] ? [] : years;
    }
    const updatedApplication = mapConfigToApplication(values.application, 'years', years);
    setValue('application', updatedApplication);
    handleRankFetchForVehcile(updatedApplication);
  };

  const selectSubconfig = (subConfig: string | null) => {
    if (subConfig && selectedSubconfig !== subConfig) {
      setSelectedSubconfig(subConfig);
      setChangeConfigActive(false);
    }
  };

  const handleRankFetchForVehcile = async (application: Application) => {
    if (
      application.hasOwnProperty('make_ids') ||
      application.hasOwnProperty('model_ids') ||
      application.hasOwnProperty('sub_model_ids') ||
      application.hasOwnProperty('years') ||
      application.hasOwnProperty('region_ids')
    ) {
      await getRankedApplications(application);
      if (!getRecommendations) setRecommendations(true);
    } else if (getRecommendations) setRecommendations(false);
  };

  const mapApplication = (subconfigName: string, subconfigId: number) => {
    const idName = convertConfigNameToIds(subconfigName);
    const mappedValueIds = values.application[idName] || [];
    const newValues = mappedValueIds.includes(subconfigId) ? [] : [subconfigId];
    const updatedApplication = mapConfigToApplication(values.application, idName, newValues);
    setValue('application', updatedApplication);
    // handle recommendation fetch status for equipment
    if (
      idName === 'mfr_ids' ||
      idName === 'equipment_model_ids' ||
      idName === 'vehicle_type_ids' ||
      idName === 'region_ids' ||
      idName === 'production_start' ||
      idName === 'production_end'
    ) {
      const eMfr = updatedApplication.hasOwnProperty('mfr_ids');
      const eModel = updatedApplication.hasOwnProperty('equipment_model_ids');
      const vehicleType = updatedApplication.hasOwnProperty('vehicle_type_ids');
      const years =
        updatedApplication.hasOwnProperty('production_start') ||
        updatedApplication.hasOwnProperty('production_end');
      const regions = updatedApplication.hasOwnProperty('region_ids');
      if ((eMfr || eModel || vehicleType || years || regions) && !getRecommendations) {
        getRankedApplications(updatedApplication).then(() => {
          setRecommendations(true);
        });
      } else if (!(eMfr || eModel || vehicleType || years || regions) && getRecommendations)
        setRecommendations(false);
    }
    if (getRecommendations) getRankedApplications(updatedApplication);
  };

  const getRankedApplications = (application: Application, vcdb?: number) => {
    const vcdbs = selectedVcdb ? [vcdb || selectedVcdb] : [];
    const equipment = vehicleEquipmentType === 'equipment';
    return dispatch(fetchRankedApplications(application, vcdbs, undefined, equipment));
  };

  const handleSubmitForm = async (values: any) => {
    const { linkConfigs, createVehicle, application, changeConfigs } = values;
    intercomEvent('viewed-company-cvt-settings', { location: 'settings', action: 'save' });
    let vehicleConfig: { [key: string]: number[] } = {};
    const {
      make_ids,
      model_ids,
      sub_model_ids,
      region_ids,
      years,
      production_start,
      production_end,
      mfr_ids,
      equipment_model_ids,
      vehicle_type_ids,
    } = application;
    if (make_ids && model_ids && sub_model_ids && region_ids && years) {
      vehicleConfig = { make_ids, model_ids, sub_model_ids, region_ids, years };
      const model = resources.models.find((model: any) => model.id === model_ids[0]);
      if (model) {
        vehicleConfig.base_model_type_id = model.vehicle_type;
      }
    } else if (mfr_ids && equipment_model_ids && vehicle_type_ids && region_ids) {
      vehicleConfig = {
        mfr_ids,
        equipment_model_ids,
        vehicle_type_ids,
        region_ids,
      };
      // year is optional
      if (production_start) vehicleConfig.production_start = production_start;
      if (production_end) vehicleConfig.production_end = production_end;
    }
    const configs: { [key: string]: { [key: string]: number[] }[] } = {};
    if (
      createVehicle ||
      changeConfigs.find((c: any) => c.configName === 'vehicles' || c.configName === 'equipments')
    ) {
      if (vehicleEquipmentType === 'vehicle') configs.vehicle_base = [vehicleConfig];
      else configs.equipment_engines = [vehicleConfig];
    }
    if (changeConfigs.length > 0) {
      await Promise.all(
        changeConfigs.map(async (config: { configName: string; id: number }) => {
          if (config.configName === 'vehicles' || config.configName === 'equipments') {
            const nameConfigs = mapConfigIdToName(configs, resourceMaps) as {
              [key: string]: { [key: string]: string }[];
            };
            await dispatch(
              changeCustomVehicle(nameConfigs, selectedVcdb!, config.id, vehicleEquipmentType)
            );
          } else {
            const configKey =
              vehicleEquipmentType === 'equipment' && config.configName === 'engines'
                ? 'equipment_engines'
                : config.configName;
            await dispatch(deleteCustomConfig(selectedVcdb!, configKey, config.id));
          }
        })
      );
    }

    const changeConfigKeys = changeConfigs
      .map((c: any) => c.configName)
      .filter((c: string) => !['vehicles', 'equipments'].includes(c));

    const configGroupKeys = [...new Set([...linkConfigs, ...changeConfigKeys])];

    const subconfigKeys = Object.keys(application);

    configGroupKeys.forEach((groupKey: string) => {
      const configPrefix = getSingularConfigGroupName(groupKey);
      const keys = subconfigKeys.filter(key => {
        if (configPrefix === 'body') return key.startsWith(configPrefix);
        return key.includes(configPrefix);
      });

      const configObj: { [key: string]: number[] } = {};

      keys.forEach(key => {
        configObj[key] = application[key];
      });

      const configName =
        vehicleEquipmentType === 'equipment' && groupKey === 'engines'
          ? 'equipment_engines'
          : groupKey;

      if (configGroupKeys.includes(groupKey))
        configs[configName] = [{ ...vehicleConfig, ...configObj }];
      else configs[configName] = [configObj];
    });

    // api expects names instead of ids
    const nameConfigs = mapConfigIdToName(configs, resourceMaps) as {
      [key: string]: { [key: string]: string }[];
    };

    return dispatch(
      updateCustomVehicleConfigs(
        nameConfigs,
        selectedVcdb!,
        linkConfigs.length > 0 || createVehicle || changeConfigs.length > 0,
        createVehicle || linkConfigs.length > 0 || changeConfigs.length > 0
      )
    )
      .then(() => {
        if (page) {
          setStatus('SUCCESS');
          reset({
            application: {} as Application,
            linkConfigs: [],
            createVehicle: false,
            selectedVcdb,
            changeConfigs: [],
          });
          setTimeout(() => setStatus('EDIT'), 2500);
          selectSubconfig('vehicle_bases');
          setRecommendations(false);
        }
        if (onClose) onClose(false);
      })
      .catch(() => {
        if (page) setStatus('ERROR');
      });
  };

  const drawerFooter = React.useMemo(() => {
    return pendingConfigCount ? (
      <div className="flex-col text-right leading-8">
        {`(${t('application:pendingNewConfig', { count: pendingConfigCount })})`}
      </div>
    ) : null;
  }, [pendingConfigCount, t]);

  const mergedRankedApplications = mergeApplicationRanks(
    values.application,
    getRecommendations ? rankedApplications : {},
    resourceMaps
  );

  const showSaveBar =
    values.createVehicle || values.linkConfigs.length > 0 || values.changeConfigs.length > 0;

  const saveButtonEnabled =
    canManageVcdb &&
    (values.createVehicle || values.linkConfigs.length > 0 || values.changeConfigs.length > 0);

  const allSubconfigValues = (configName: string) => {
    // get all values to a specific config name e.g. position
    // resourcesMapping contains name mapping e.g. engine_mfrs => mfrs, transmission_mfrs => mfrs
    if (resourcesMapping.hasOwnProperty(configName)) {
      configName = resourcesMapping[configName];
    }
    return resources && resources[configName] ? resources[configName] : [];
  };

  const resetEquipment = () => {
    /*  eslint-disable @typescript-eslint/no-unused-vars */
    const {
      mfr_ids,
      equipment_model_ids,
      vehicle_type_ids,
      region_ids,
      production_end,
      production_start,
      ...rest
    } = values.application;
    /*  eslint-enable @typescript-eslint/no-unused-vars */
    setValue('application', rest);
    setValue('changeConfigs', []);
    setValue('linkConfigs', []);
    setValue('createVehicle', false);
    setChangeConfigActive(false);
  };

  const resetVehicle = () => {
    const engineKeys = Object.keys(values.application).filter(key => key.includes('engine'));
    if (engineKeys.length > 0) {
      const newApplication: { [key: string]: string } = {};
      engineKeys.forEach(key => (newApplication[key] = values.application[key]));
      setValue('application', newApplication as Application);
    } else setValue('application', {} as Application);
    setValue('changeConfigs', []);
    setValue('linkConfigs', []);
    setValue('createVehicle', false);
    setChangeConfigActive(false);
  };

  const addCustomValue = (configName: string, value: string, type?: string, group?: string) => {
    if (!selectedVcdb) {
      message.error(t('validation:required'));
      return Promise.resolve();
    }

    const configGroupKey = getConfigGroupName(configName);

    const configKey =
      vehicleEquipmentType === 'equipment' && configName === 'vehicle_types'
        ? 'vehicle_type_id'
        : convertConfigNameToId(configName);

    const configs: { [key: string]: { [key: string]: string }[] } = {
      [configGroupKey!]: [
        {
          [configKey]: value,
          ...(group && configName === 'vehicle_types'
            ? { vehicle_type_id: type, vehicle_type_group_id: group }
            : {}),
          ...(group && configName === 'models'
            ? { base_model_type_id: type, vehicle_type_group_id: group }
            : {}),
        },
      ],
    };
    return dispatch(addCustomSubConfig(configs, selectedVcdb!))
      .then(() => dispatch(fetchApplicationResources()))
      .catch(() => {
        message.error(t('common:errorSaving'));
      });
  };

  const deleteCustomValue = (configName: string, id: number) => {
    modal.confirm({
      title: t('application:deleteCustomSubConfig'),
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: t('common:delete'),
      onOk() {
        // TODO: remove special case for deleting equipment type when backend adjusted
        const configIdName =
          configName === 'vehicle_types' ? 'base_model_type_id' : convertConfigNameToId(configName);
        dispatch(deleteCustomSubConfig(selectedVcdb!, configIdName, id));
      },
    });
  };
  const deleteCustomConfigById = (configName: string, id: number) => {
    modal.confirm({
      title: t('application:deleteCustomConfig'),
      icon: <ExclamationCircleOutlined />,
      okType: 'danger',
      okText: t('common:delete'),
      onOk() {
        dispatch(deleteCustomConfig(selectedVcdb!, configName, id)).then(() =>
          getRankedApplications(values.application)
        );
      },
    });
  };

  const linkConfig = (name: string) => {
    const newValues = [name, ...values.linkConfigs];
    setValue('linkConfigs', newValues);
    setPendingConfigCount(newValues.length);
  };

  const createVehicle = (status: boolean) => {
    setValue('createVehicle', status);
  };

  const changeConfiguration = (status: boolean) => {
    setChangeConfigActive(status);
  };

  const confirmChangeConfiguration = (configName: string, id: number) => {
    modal.confirm({
      title: t('application:confirmConfiguration'),
      icon: <ExclamationCircleOutlined />,
      content: t('application:confirmChangeInfo'),
      okText: t('common:confirm'),
      onOk() {
        setValue('changeConfigs', [...values.changeConfigs, { configName, id }]);
        changeConfiguration(false);
      },
    });
  };

  const selectVcdb = (id?: number) => {
    setSelectedVcdb(id);
    reset({
      application: values.application,
      linkConfigs: [],
      createVehicle: false,
      selectedVcdb: id,
      changeConfigs: [],
    });
    getRankedApplications(values.application, id);
  };

  const details = (
    <ApplicationDetails
      singleApplicationEdit
      hideSearch
      vcdbView
      disableAddDelete={!canManageVcdb}
      addCustomValue={addCustomValue}
      deleteCustomValue={deleteCustomValue}
      deleteCustomConfigById={deleteCustomConfigById}
      application={values.application}
      applicationStructure={applicationStructure}
      getRankedApplications={getRankedApplications}
      getRecommendations={getRecommendations}
      rankedApplications={mergedRankedApplications}
      allSubconfigValues={allSubconfigValues}
      selectedSubconfig={selectedSubconfig}
      selectedVcdb={selectedVcdb}
      selectVcdb={selectVcdb}
      selectMake={selectMake}
      selectModel={selectModel}
      selectType={selectType}
      selectSubmodel={selectSubmodels}
      selectRegion={selectRegion}
      selectYears={selectYears}
      selectSubconfig={selectSubconfig}
      mapConfigToApplication={mapApplication}
      setRecommendations={setRecommendations}
      resetEquipment={resetEquipment}
      resetVehicle={resetVehicle}
      linkConfig={linkConfig}
      createVehicle={createVehicle}
      changeConfiguration={changeConfiguration}
      confirmChangeConfiguration={confirmChangeConfiguration}
      selectedCustomVcdb={selectedVcdb}
      linkConfigs={values.linkConfigs}
      createVehicleStatus={values.createVehicle}
      changeConfigActive={changeConfigActive}
      vehicleEquipmentType={vehicleEquipmentType}
      setVehicleEquipment={(type: string) => setVehicleEquipmentType(type)}
    />
  );

  const show = (showSaveBar && status === 'EDIT') || status === 'SUCCESS' || status === 'ERROR';

  return (
    <FormProvider {...formMethods}>
      {page ? (
        <Page
          contentNoScroll
          renderBottom={
            show ? (
              <React.Fragment>
                <div
                  className={classNames('status-bar flex items-center', {
                    success: status === 'SUCCESS',
                    error: status === 'ERROR',
                  })}
                >
                  {status === 'ERROR' && <Tag color="red">{t('common:errorSaving')}</Tag>}
                </div>
                <div className="page__bottom-save flex items-center">
                  <div className="flex items-center flex-1">
                    {!formState.isValid && formState.submitCount > 0 && (
                      <Tag color="red">{t('common:fieldsMissing')}</Tag>
                    )}
                    <div className="flex-1 flex justify-end">
                      {drawerFooter}
                      <Button
                        className="page__bottom-save-cancel"
                        onClick={() => {
                          reset();
                          setStatus('EDIT');
                          selectSubconfig('vehicle_bases');
                          setRecommendations(false);
                        }}
                      >
                        {t('common:cancel')}
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleSubmit(handleSubmitForm)}
                        disabled={!saveButtonEnabled}
                        loading={formState.isSubmitting}
                      >
                        {t('common:save')}
                      </Button>
                    </div>
                  </div>
                </div>
                <FormRouterPrompt formDirty={saveButtonEnabled} />
              </React.Fragment>
            ) : null
          }
        >
          {details}
        </Page>
      ) : (
        <DrawerSave
          title={t('application:vcdbDrawerTitle')}
          className="application__details-drawer"
          onClose={force => (onClose ? onClose(formState.isDirty, force) : null)}
          onSave={handleSubmit(handleSubmitForm)}
          visible={visible ?? false}
          width="80%"
          saveButtonEnabled={saveButtonEnabled}
          additionalFooterData={drawerFooter}
          isSubmitting={formState.isSubmitting}
          destroyOnClose
        >
          {details}
        </DrawerSave>
      )}
    </FormProvider>
  );
};

export default VcdbEdit;
